<template>
  <div class="w-full p-6">
    <h2 class="text-2xl font-bold mb-2">Email Generator</h2>
    <p class="text-muted-foreground mb-4">Create and manage automated email communications based on reporting calendars</p>
    
    <!-- File Upload Section -->
    <div class="bg-white shadow-md rounded-lg p-6 mb-6">
      <h3 class="text-xl font-bold mb-4">1. Upload Reporting Calendar</h3>
      
      <div class="mb-4">
        <label class="block text-gray-700 text-sm font-bold mb-2" for="calendar-file">
          Select Excel File
        </label>
        <input
          type="file"
          id="calendar-file"
          accept=".xlsx, .xls"
          @change="handleFileUpload"
          class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        />
      </div>
      
      <div v-if="uploadError" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
        {{ uploadError }}
      </div>
      
      <button
        @click="parseFile"
        :disabled="!selectedFile"
        class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
      >
        Parse Calendar
      </button>
    </div>
    <!-- Saved Drafts Section -->
    <div class="bg-white shadow-md rounded-lg p-6 mb-6">
      <h3 class="text-xl font-bold mb-4">2. Saved Drafts</h3>
      
      <div v-if="savedDrafts.length > 0" class="overflow-x-auto">
        <table class="min-w-full bg-white">
          <thead>
            <tr>
              <th class="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Subject
              </th>
              <th class="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Created At
              </th>
              <th class="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="draft in savedDrafts" :key="draft.id">
              <td class="py-2 px-4 border-b border-gray-200">
                {{ draft.subject }}
              </td>
              <td class="py-2 px-4 border-b border-gray-200">
                {{ formatDate(draft.createdAt) }}
              </td>
              <td class="py-2 px-4 border-b border-gray-200">
                <button
                  @click="loadEmailDraft(draft.id)"
                  class="text-blue-500 hover:text-blue-700 mr-2"
                >
                  Load
                </button>
                <button
                  @click="showDeleteConfirmation(draft.id, draft.subject)"
                  class="text-red-500 hover:text-red-700"
                >
                  Delete
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <EmptyState
        v-else
        icon="document"
        title="No Saved Drafts"
        message="You haven't saved any email drafts yet. After creating a draft, click 'Save Draft' to save it here."
        iconClass="text-gray-400"
      />
    </div>
    
    <!-- Email Preview Section -->
    <div v-if="emailDraft" class="bg-white shadow-md rounded-lg p-6 mb-6">
      <h3 class="text-xl font-bold mb-4">3. Email Draft Preview</h3>
      
      <div class="mb-4">
        <label class="block text-gray-700 text-sm font-bold mb-2" for="email-subject">
          Subject
        </label>
        <input
          type="text"
          id="email-subject"
          v-model="emailDraft.subject"
          class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        />
      </div>
      
      <div class="mb-4">
        <label class="block text-gray-700 text-sm font-bold mb-2">
          HTML Content
        </label>
        <div class="border rounded p-4 bg-gray-50 h-64 overflow-auto relative">
          <div v-html="processedHtmlContent"></div>
          <button
            @click="showPreviewModal = true"
            class="absolute top-2 right-2 bg-white p-1 rounded-md shadow hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            title="View in full screen"
          >
            <svg class="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"></path>
            </svg>
          </button>
        </div>
      </div>
      
      <div class="mb-4">
        <label class="block text-gray-700 text-sm font-bold mb-2">
          Text Content
        </label>
        <textarea
          v-model="emailDraft.textContent"
          class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline h-32"
          readonly
        ></textarea>
      </div>
      
      <!-- Recipient input for email draft -->
      <div class="mb-4">
        <label class="block text-gray-700 text-sm font-bold mb-2" for="draft-recipient">
          Recipient Email
        </label>
        <input
          id="draft-recipient"
          v-model="emailDraftRecipient"
          type="email"
          placeholder="Enter recipient email address"
          class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        />
      </div>
      
      <!-- CC input for email draft -->
      <div class="mb-4">
        <label class="block text-gray-700 text-sm font-bold mb-2" for="draft-cc">
          CC Email
        </label>
        <input
          id="draft-cc"
          v-model="emailDraftCC"
          type="email"
          placeholder="Enter CC email addresses (comma separated)"
          class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        />
      </div>
      
      <div class="flex justify-end space-x-3">
        <button
          @click="clearEmailDraft"
          class="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
        >
          Clear
        </button>
        
        <button
          @click="saveEmailDraft"
          class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
        >
          Save Draft
        </button>
        
        <button
          @click="sendEmailDraft"
          :disabled="!emailDraft || !emailDraftRecipient || isEmailDraftSending"
          class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
        >
          <span v-if="isEmailDraftSending">Sending...</span>
          <span v-else>Send Draft</span>
        </button>
      </div>
      
      <div v-if="emailDraftStatus.message" :class="['mt-4 p-4 rounded', emailDraftStatus.isError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700']">
        {{ emailDraftStatus.message }}
      </div>
    </div>
    
    <!-- Test Email Section -->
    <div class="bg-white shadow-md rounded-lg p-6 mb-6">
      <h3 class="text-xl font-bold mb-4">Send Test Email (Delete Later)</h3>
      
      <!-- Recipient Mode Toggle -->
      <div class="mb-4">
        <label class="block text-gray-700 text-sm font-bold mb-2">
          Recipient Mode
        </label>
        <div class="flex items-center space-x-4">
          <label class="inline-flex items-center">
            <input
              type="radio"
              v-model="recipientMode"
              value="single"
              class="form-radio h-4 w-4 text-blue-600"
            >
            <span class="ml-2">Single Recipient (to)</span>
          </label>
          <label class="inline-flex items-center">
            <input
              type="radio"
              v-model="recipientMode"
              value="multiple"
              class="form-radio h-4 w-4 text-blue-600"
            >
            <span class="ml-2">Multiple Recipients (bcc)</span>
          </label>
        </div>
      </div>
      
      <!-- Single Recipient Input (shown when recipientMode is 'single') -->
      <div v-if="recipientMode === 'single'" class="mb-4">
        <label class="block text-gray-700 text-sm font-bold mb-2" for="test-email">
          Recipient Email
        </label>
        <input
          type="email"
          id="test-email"
          v-model="testEmailRecipient"
          placeholder="Enter recipient email address"
          class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        />
        <p class="mt-1 text-xs text-gray-500">The recipient will see they are the direct recipient of this email.</p>
      </div>
      
      <!-- Multiple Recipients Input (shown when recipientMode is 'multiple') -->
      <div v-if="recipientMode === 'multiple'" class="mb-4">
        <label class="block text-gray-700 text-sm font-bold mb-2" for="test-email-bcc">
          Recipients Emails
        </label>
        <input
          type="text"
          id="test-email-bcc"
          v-model="testEmailBcc"
          placeholder="Enter recipients comma-separated email addresses"
          class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        />
        <p class="mt-1 text-xs text-gray-500">Recipients won't see each other's addresses. Format: email1@example.com,email2@example.com</p>
      </div>
      
      <div class="mb-4">
        <label class="block text-gray-700 text-sm font-bold mb-2" for="test-email-body">
          Email Body
        </label>
        <textarea
          id="test-email-body"
          v-model="testEmailBody"
          placeholder="Enter email content"
          rows="4"
          class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        ></textarea>
      </div>
      
      <!-- File Attachments for Test Email -->
      <div class="mb-4">
        <label class="block text-gray-700 text-sm font-bold mb-2" for="test-email-attachment">
          Attachments
        </label>
        <input
          type="file"
          id="test-email-attachment"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
          @change="handleTestAttachmentUpload"
          class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        />
        
        <!-- List of attached files -->
        <div v-if="testAttachments.length > 0" class="mt-4">
          <h4 class="font-medium text-sm mb-2">Attached Files:</h4>
          <ul class="space-y-2">
            <li
              v-for="(file, index) in testAttachments"
              :key="index"
              class="flex items-center justify-between bg-gray-50 p-2 rounded border"
            >
              <div class="flex items-center">
                <svg class="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                <span class="text-sm truncate max-w-xs">{{ file.name }}</span>
                <span class="text-xs text-gray-500 ml-2">({{ formatFileSize(file.size) }})</span>
              </div>
              <button
                @click="removeTestAttachment(index)"
                class="text-red-500 hover:text-red-700"
                title="Remove attachment"
              >
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </li>
          </ul>
        </div>
      </div>
      
      <div v-if="emailStatus.message" :class="['p-4 rounded mb-4', emailStatus.isError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700']">
        {{ emailStatus.message }}
      </div>
      
      <button
        @click="sendTestEmail"
        :disabled="isEmailSending ||
                  (recipientMode === 'single' && !testEmailRecipient) ||
                  (recipientMode === 'multiple' && !testEmailBcc) ||
                  !testEmailBody.trim()"
        class="bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
      >
        <span v-if="isEmailSending">Sending...</span>
        <span v-else>Send Test Email</span>
      </button>
    </div>
    
    <!-- Delete Confirmation Modal -->
    <ConfirmationModal
      v-model:isOpen="showDeleteModal"
      title="Delete Email Draft"
      :message="`Are you sure you want to delete the email draft '${draftToDeleteSubject}'?`"
      confirmButtonText="Delete"
      cancelButtonText="Cancel"
      confirmButtonClass="bg-red-600 hover:bg-red-700 text-white focus:ring-red-500"
      iconType="warning"
      @confirm="confirmDelete"
    />
    
    <!-- Email Preview Modal -->
    <EmailPreviewModal
      v-model:isOpen="showPreviewModal"
      :subject="emailDraft?.subject || ''"
      :htmlContent="emailDraft?.htmlContent || ''"
      :textContent="emailDraft?.textContent || ''"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, reactive } from 'vue';
import { ConfirmationModal, EmptyState } from '../../../shared/ui';
import { EmailDraft } from '../models/types';
import {
  CalendarParserService,
  EmailStorageService,
  EmailTemplateService,
  LoggingService
} from '../services';
import { formatDate } from '../utils/dateFormatter';
import EmailPreviewModal from './EmailPreviewModal.vue';
import { logger, LogCategory } from '../../../services/logger';
import { emailService } from '../../../services/emailService';

export default defineComponent({
  name: 'EmailGeneratorView',
  components: {
    ConfirmationModal,
    EmptyState,
    EmailPreviewModal
  },
  
  setup() {
    // Services
    const calendarParser = new CalendarParserService();
    const emailTemplate = new EmailTemplateService();
    const emailStorage = new EmailStorageService();
    const loggingService = new LoggingService();
    
    // State
    const selectedFile = ref<File | null>(null);
    const uploadError = ref<string | null>(null);
    const emailDraft = ref<EmailDraft | null>(null);
    const savedDrafts = ref<EmailDraft[]>([]);
    
    // Test email state
    const recipientMode = ref<'single' | 'multiple'>('single'); // Default to single recipient mode
    const testEmailRecipient = ref<string>('');
    const testEmailBcc = ref<string>('');
    const testEmailBody = ref<string>('This is a test email sent from our verified domain.');
    const testAttachments = ref<File[]>([]);
    const isEmailSending = ref<boolean>(false);
    const emailStatus = reactive({
      message: '',
      isError: false
    });
    
    // Email draft sending state
    const emailDraftRecipient = ref<string>('');
    const emailDraftCC = ref<string>('');
    const isEmailDraftSending = ref<boolean>(false);
    const emailDraftStatus = reactive({
      message: '',
      isError: false
    });
    
    // Process HTML content to fix logo display
    const processedHtmlContent = computed(() => {
      if (!emailDraft.value) return '';
      
      // Inline SVG for Fraport logo
      const fraportLogoSvg = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:svg="http://www.w3.org/2000/svg" width="284.25531mm" height="122.08274mm" viewBox="0 0 284.25531 122.08274" version="1.1" id="svg8"><g transform="translate(72.99477,-76.279161)" id="layer1"><path id="path847" d="m 116.87023,197.9848 c 0,-0.48219 0.99424,-6.08414 2.09317,-11.79373 0.47054,-2.44475 1.24539,-6.58813 1.72185,-9.2075 0.47648,-2.61938 1.28173,-6.95325 1.78943,-9.63084 0.5077,-2.67758 0.98922,-5.32077 1.07002,-5.87375 l 0.14694,-1.00541 h 2.93399 2.93402 l -0.10871,0.58208 c -0.58698,3.14257 -0.68616,3.76303 -0.61322,3.83596 0.0463,0.0463 0.7777,-0.61135 1.62541,-1.46138 1.85716,-1.86225 3.00706,-2.62445 4.79147,-3.17592 2.71742,-0.83983 5.50162,-0.40024 7.17618,1.13301 3.57243,3.27094 3.73281,11.10599 0.35321,17.25442 -1.62626,2.95858 -3.4616,4.86941 -5.98746,6.23368 -3.58616,1.93697 -7.03605,1.8451 -10.05215,-0.26768 -0.66804,-0.46796 -1.29337,-0.85084 -1.38962,-0.85084 -0.0962,0 -0.23909,0.50006 -0.31745,1.11125 -0.0784,0.61119 -0.51143,3.01625 -0.96239,5.34458 -0.45093,2.32834 -0.9805,5.1144 -1.17678,6.19125 l -0.35689,1.95792 h -2.83551 c -2.57852,0 -2.83551,-0.0342 -2.83551,-0.3771 z m 16.62854,-16.24537 c 1.93759,-1.19793 4.07115,-4.43616 4.87678,-7.4017 0.45578,-1.67777 0.63861,-6.13616 0.30594,-7.45976 -0.48584,-1.93287 -1.74348,-3.01774 -3.49839,-3.01774 -1.82846,0 -3.39513,0.98842 -4.89235,3.0866 -1.93047,2.7053 -3.29193,6.3573 -4.0037,10.73953 l -0.372,2.2903 0.8063,0.82573 c 1.28073,1.31159 2.25147,1.69468 4.1355,1.63202 1.36324,-0.0453 1.75994,-0.14969 2.64192,-0.69498 z m -36.84434,4.17711 c -2.15071,-0.74371 -3.56181,-2.35197 -4.39955,-5.01426 -0.67751,-2.15305 -0.6665,-6.64215 0.0226,-9.21038 1.305,-4.86384 4.46575,-8.96791 8.30498,-10.78358 3.76017,-1.77828 8.04424,-1.25665 9.82569,1.19637 0.3527,0.48566 0.70513,0.88883 0.78319,0.89594 0.0781,0.007 0.19713,-0.27281 0.26459,-0.62206 0.0675,-0.34925 0.17786,-0.92075 0.24532,-1.27 l 0.12264,-0.635 h 2.96947 2.96945 l -0.11574,0.47625 c -0.13841,0.56953 -0.74748,3.81564 -2.47222,13.17625 -0.70788,3.84175 -1.4866,8.00893 -1.73051,9.26041 l -0.44346,2.27542 h -2.90906 -2.90904 l 0.28786,-1.53458 c 0.15835,-0.84402 0.3396,-1.7489 0.40278,-2.01084 0.0632,-0.26193 0.0591,-0.47625 -0.009,-0.47625 -0.0682,0 -0.69026,0.50007 -1.38233,1.11125 -3.40986,3.01145 -6.96273,4.1557 -9.8275,3.16506 z m 7.39545,-4.36244 c 2.62433,-1.53796 4.95901,-5.87389 5.93714,-11.02637 0.40928,-2.1559 0.40746,-3.51214 -0.006,-4.50182 -0.71117,-1.70208 -1.90409,-2.45314 -3.90573,-2.45906 -1.1194,-0.003 -1.58337,0.10905 -2.48937,0.60287 -2.31163,1.25998 -4.26274,4.09018 -5.16592,7.49347 -0.53127,2.00189 -0.65553,6.66272 -0.2111,7.91867 0.36334,1.02684 1.21519,2.03503 2.03506,2.40859 0.92602,0.42192 2.68571,0.22017 3.80597,-0.43635 z m 52.82535,4.61503 c -2.11616,-0.39248 -4.66617,-1.76597 -5.97204,-3.21669 -1.98205,-2.20187 -2.71696,-4.11893 -2.8651,-7.47384 -0.13024,-2.94925 0.22589,-4.85443 1.34325,-7.18599 2.60069,-5.42671 7.24273,-8.46019 12.93318,-8.45153 2.94974,0.004 5.13878,0.88489 7.11579,2.86191 2.23331,2.2333 3.23568,5.26872 3.03828,9.20058 -0.20892,4.1611 -1.62486,7.56744 -4.24745,10.21813 -2.60771,2.63565 -5.46648,3.94524 -8.89231,4.07352 -1.05844,0.0396 -2.16256,0.0279 -2.4536,-0.0261 z m 4.07114,-3.8016 c 2.01172,-0.68547 3.90671,-3.41479 4.84035,-6.97146 0.60862,-2.31852 0.6779,-6.85787 0.13163,-8.62448 -0.41182,-1.33174 -1.33254,-2.53039 -2.35411,-3.06465 -0.88083,-0.46066 -2.97512,-0.45104 -3.95696,0.0182 -1.9946,0.95321 -4.16776,4.12237 -4.95497,7.22595 -0.97064,3.82673 -0.36195,8.64534 1.33322,10.55457 0.97907,1.10267 3.14817,1.47953 4.96084,0.86189 z m 38.79136,3.80127 c -2.66564,-0.6757 -3.90315,-2.14245 -3.91005,-4.6344 -0.004,-1.39381 0.29187,-3.26201 1.88959,-11.93677 0.58737,-3.18914 1.06796,-5.83233 1.06796,-5.87375 0,-0.0414 -0.66675,-0.0753 -1.48166,-0.0753 -0.9888,0 -1.48167,-0.0823 -1.48167,-0.24725 0,-0.13598 0.10435,-0.84652 0.2319,-1.57896 l 0.23193,-1.33171 1.61347,-0.0615 1.61347,-0.0615 0.46888,-2.81528 c 0.25788,-1.54841 0.50474,-2.92292 0.5486,-3.05446 0.0712,-0.21359 6.08675,-1.88803 6.08675,-1.69426 0,0.0436 -0.28575,1.6055 -0.635,3.47092 -0.34925,1.86542 -0.635,3.5733 -0.635,3.7953 0,0.37782 0.18936,0.40362 2.96333,0.40362 1.62984,0 2.95747,0.0714 2.95028,0.15875 -0.007,0.0873 -0.1309,0.77787 -0.27492,1.53458 l -0.26187,1.37583 -3.05022,0.10584 -3.05022,0.10583 -0.47577,2.43417 c -1.38428,7.08215 -2.39992,12.91614 -2.39947,13.7827 0.002,1.99876 1.32863,2.72592 3.97766,2.17875 0.86377,-0.17842 1.62003,-0.27485 1.68059,-0.21429 0.0606,0.0606 0.17526,0.73956 0.25489,1.50888 l 0.14478,1.39876 -0.72718,0.30383 c -1.91379,0.79964 -5.9828,1.36589 -7.34105,1.0216 z M -7.3783497,183.06898 c -5e-4,-4.96395 -0.89078,-10.68861 -2.3454,-15.08125 -2.8082203,-8.48021 -7.5066103,-13.29391 -14.3760203,-14.72886 -7.24081,-1.51253 -19.09232,0.50658 -33.44928,5.69868 -4.07676,1.47433 -9.3375,3.56034 -11.68442,4.63315 -1.73649,0.79377 -2.37822,0.77579 -1.35982,-0.0381 0.79432,-0.63481 7.40511,-4.56098 10.61602,-6.30489 11.0503,-6.00164 21.91788,-10.10716 30.48,-11.51463 3.50174,-0.57562 10.31925,-0.53725 12.91167,0.0727 2.51953,0.59279 3.89691,1.1283 5.85786,2.2775 4.0438603,2.36988 6.8177703,6.64181 7.7235203,11.89454 0.47188,2.73653 0.47116,8.19081 -0.002,11.60827 -0.70106,5.06855 -3.30553,14.07583 -4.07006,14.07583 -0.24548,0 -0.30234,-0.48731 -0.30254,-2.59292 z m 60.7485897,2.39143 c 0,-0.11082 0.42559,-2.39625 0.94576,-5.07874 0.52018,-2.68249 1.09339,-5.75888 1.2738,-6.83643 0.18042,-1.07754 0.41977,-2.3878 0.5319,-2.91167 0.21215,-0.99113 3.21788,-17.30132 3.53015,-19.15584 l 0.17821,-1.05833 8.54291,-0.0553 8.54291,-0.0553 -0.12572,0.47867 c -0.0691,0.26327 -0.24199,1.16924 -0.3841,2.01326 l -0.25837,1.53458 h -5.22448 c -3.92859,0 -5.27558,0.0656 -5.4305,0.26459 -0.11331,0.14552 -0.60925,2.50295 -1.10209,5.23875 -0.49284,2.73579 -0.94599,5.18848 -1.007,5.45041 l -0.11094,0.47625 h 5.23233 5.23234 l -0.1215,0.68792 c -0.0668,0.37835 -0.23008,1.33085 -0.36278,2.11667 l -0.24129,1.42875 h -5.24623 -5.24624 l -0.66159,3.54541 c -1.03032,5.52144 -2.13748,11.69372 -2.13748,11.91622 0,0.11886 -1.30401,0.2017 -3.175,0.2017 -1.86867,0 -3.175,-0.0829 -3.175,-0.20149 z m 20.6519,-1.33309 c 0.15458,-0.84402 1.04208,-5.63034 1.97223,-10.63625 0.93015,-5.00592 1.79549,-9.7208 1.92297,-10.4775 0.12748,-0.75671 0.28386,-1.63777 0.34749,-1.95792 l 0.11571,-0.58208 h 2.96078 c 2.71911,0 2.95211,0.0302 2.85461,0.37041 -0.3011,1.05062 -0.78828,3.91422 -0.68371,4.01879 0.0662,0.0662 0.32992,-0.14591 0.58597,-0.47143 3.04034,-3.86516 3.6669,-4.38158 5.47474,-4.51235 1.04644,-0.0757 1.45209,0.003 2.39181,0.46251 2.00627,0.98158 2.00727,0.98881 0.4672,3.37412 -0.7367,1.14101 -1.39442,2.12955 -1.46161,2.19674 -0.0672,0.0672 -0.68247,0.0119 -1.36729,-0.1228 -1.78892,-0.35195 -3.12359,-0.025 -4.36727,1.06968 -2.04163,1.79712 -2.90511,4.19043 -4.46642,12.37958 -0.63385,3.32454 -1.15245,6.12977 -1.15245,6.23385 0,0.10408 -1.32205,0.18923 -2.9379,0.18923 h -2.9379 z m 99.78643,1.34621 c 0,-0.1036 0.18774,-1.10088 0.41723,-2.21617 0.22949,-1.11529 0.61481,-3.19747 0.85628,-4.62705 0.24145,-1.42958 0.90557,-5.02812 1.47582,-7.99674 0.57026,-2.96863 1.18671,-6.25475 1.36995,-7.3025 0.18322,-1.04775 0.38092,-2.11932 0.4393,-2.38125 0.10365,-0.46509 0.17303,-0.47625 2.95963,-0.47625 3.2909,0 3.06415,-0.21739 2.51963,2.41551 -0.18357,0.88777 -0.29872,1.71938 -0.25584,1.84801 0.0429,0.12864 0.81185,-0.67855 1.70881,-1.79374 2.10394,-2.6158 3.01767,-3.1872 4.90457,-3.06705 1.11396,0.0709 1.54282,0.21548 2.42678,0.81796 0.59053,0.40249 1.07387,0.81411 1.0741,0.9147 0,0.29565 -2.72434,4.36794 -2.92284,4.36794 -0.0996,0 -0.7126,-0.10673 -1.36214,-0.23718 -0.99225,-0.19927 -1.36665,-0.17879 -2.34249,0.12816 -1.57978,0.4969 -2.48249,1.30221 -3.37236,3.0085 -1.19554,2.29238 -1.9518,5.05957 -3.03998,11.12344 l -1.01608,5.66208 h -2.92018 c -1.6061,0 -2.92019,-0.0848 -2.92019,-0.18837 z M 27.0345,177.13711 c -0.87434,-1.49196 -2.3441,-4.76762 -3.25048,-7.24438 -2.74053,-7.4887 -3.10358,-15.87661 -0.96427,-22.27838 1.31545,-3.93638 3.46099,-7.27715 6.83648,-10.64494 10.08479,-10.06176 28.88952,-15.27418 53.13567,-14.7285 3.31787,0.0747 6.31825,0.17584 6.6675,0.22481 0.34925,0.049 1.98313,0.20367 3.63085,0.34376 2.72067,0.23132 3.72877,0.43378 3.45385,0.69364 -0.0548,0.0518 -2.29045,0.30072 -4.96803,0.55309 -24.65769,2.32397 -43.78644,8.30709 -52.29465,16.35679 -5.0439,4.77207 -7.99608,10.14353 -10.03657,18.2614 -1.14044,4.5371 -1.36961,6.77118 -1.37331,13.38792 -0.002,3.28877 -0.0716,5.97958 -0.15507,5.97958 -0.0834,0 -0.39033,-0.40716 -0.68197,-0.90479 z M -72.99477,148.52186 c 0,-0.15048 0.30957,-0.39718 0.68792,-0.54822 1.81335,-0.72389 10.93917,-5.26121 14.08461,-7.00281 12.98486,-7.18958 20.5709,-13.34799 23.39739,-18.99418 1.73228,-3.46039 1.75361,-6.06859 0.077,-9.41835 -1.05745,-2.11272 -3.62822,-4.79332 -6.23233,-6.49859 -2.53519,-1.66014 -2.38427,-1.7751 1.24839,-0.95102 2.82266,0.64032 6.29889,1.79592 8.04832,2.67549 7.32524,3.68292 9.48209,8.54638 6.44358,14.52952 -3.74742,7.37903 -13.32255,14.26603 -27.96404,20.11339 -7.40297,2.95652 -19.79084,6.77148 -19.79084,6.09477 z M 59.9319,109.9654 c -4.22395,-0.38256 -9.34231,-1.51422 -11.71554,-2.59029 -4.16769,-1.88973 -6.74499,-4.85354 -7.04869,-8.105767 -0.35057,-3.75423 2.43014,-8.82906 7.75429,-14.15167 3.9874,-3.98624 10.37494,-8.99685 10.37494,-8.13847 0,0.0627 -0.88895,1.25393 -1.97545,2.64722 -3.35682,4.30469 -5.47896,8.24084 -6.41696,11.90221 -0.51211,1.99894 -0.31838,4.57725 0.4626,6.15677 2.10563,4.258637 8.8304,6.752877 20.62981,7.651657 5.34538,0.40716 15.05981,0.4028 21.7076,-0.01 4.86251,-0.30176 5.65565,-0.29811 5.33302,0.0245 -0.1501,0.1501 -5.52433,1.36351 -9.21322,2.08019 -3.05911,0.59433 -10.30708,1.6387 -14.86406,2.14178 -2.90495,0.3207 -12.92758,0.58186 -15.02834,0.3916 z M -7.4094797,105.413 c -2.30829,-0.20425 -6.2975403,-1.16963 -8.0670103,-1.95217 -3.62713,-1.60409 -6.50489,-4.061467 -8.16342,-6.970907 -1.09166,-1.91503 -1.27769,-2.47969 -0.81697,-2.47969 0.18,0 1.27308,0.3686 2.42907,0.8191 2.62897,1.02454 5.49595,1.78364 9.04055,2.3937 2.28286,0.3929 3.5630503,0.47458 7.5141603,0.47943 4.90319002,0.006 6.64614,-0.1798 10.90084,-1.16212 9.6375097,-2.22511 21.4482097,-9.22732 30.6396397,-18.16533 1.25196,-1.21745 2.33076,-2.15909 2.39732,-2.09253 0.15581,0.1558 -0.98132,2.77627 -2.08988,4.81609 -6.51308,11.98438 -18.5701,20.633337 -32.7660297,23.504347 -2.69911998,0.54587 -7.30006,1.09271 -8.55272,1.01653 -0.23283,-0.0142 -1.34233,-0.10706 -2.46555,-0.20645 z" style="fill:#003f8a;fill-opacity:1;stroke-width:0.211667" /></g></svg>`;
      
      // Convert SVG to data URL
      const svgDataUrl = `data:image/svg+xml;base64,${btoa(fraportLogoSvg)}`;
      
      // Replace CID reference with data URL
      return emailDraft.value.htmlContent.replace(
        'src="cid:fraport-logo"',
        `src="${svgDataUrl}"`
      );
    });
    
    // Modal states
    const showDeleteModal = ref<boolean>(false);
    const showPreviewModal = ref<boolean>(false);
    const draftToDeleteId = ref<string | null>(null);
    const draftToDeleteSubject = ref<string>('');
    
    // Load saved drafts on component mount
    onMounted(() => {
      loadSavedDrafts();
    });
    
    // Methods
    const handleFileUpload = (event: Event) => {
      const input = event.target as HTMLInputElement;
      if (input.files && input.files.length > 0) {
        selectedFile.value = input.files[0];
        uploadError.value = null;
      }
    };
    
    const parseFile = async () => {
      if (!selectedFile.value) {
        uploadError.value = 'Please select a file first';
        return;
      }
      
      try {
        // Parse the Excel file
        const meetingInfo = await calendarParser.parseExcelFile(selectedFile.value);
        
        // Generate email draft
        const draft = emailTemplate.generateEmailDraft(meetingInfo, 'current-user');
        
        // Set the email draft
        emailDraft.value = draft;
        
        // Log the email generation
        loggingService.logEmailGeneration(draft, 'current-user', 'create');
      } catch (error) {
        uploadError.value = `Error parsing file: ${error instanceof Error ? error.message : String(error)}`;
        logger.error('Error parsing file', LogCategory.GENERAL, { error });
      }
    };
    
    const saveEmailDraft = () => {
      if (emailDraft.value) {
        // Save the email draft
        emailStorage.saveEmailDraft(emailDraft.value);
        
        // Log the save action
        loggingService.logEmailGeneration(emailDraft.value, 'current-user', 'save');
        
        // Reload saved drafts
        loadSavedDrafts();
      }
    };
    
    const loadSavedDrafts = () => {
      savedDrafts.value = emailStorage.listEmailDrafts();
    };
    
    const loadEmailDraft = (id: string) => {
      const draft = emailStorage.getEmailDraft(id);
      if (draft) {
        emailDraft.value = draft;
        
        // Log the load action
        loggingService.logEmailGeneration(draft, 'current-user', 'load');
      }
    };
    
    // Show delete confirmation modal
    const showDeleteConfirmation = (id: string, subject: string) => {
      draftToDeleteId.value = id;
      draftToDeleteSubject.value = subject;
      showDeleteModal.value = true;
    };
    
    // Confirm delete action
    const confirmDelete = () => {
      if (draftToDeleteId.value) {
        const draft = emailStorage.getEmailDraft(draftToDeleteId.value);
        if (draft) {
          // Log the delete action
          loggingService.logEmailGeneration(draft, 'current-user', 'delete');
          
          // Delete the draft
          emailStorage.deleteEmailDraft(draftToDeleteId.value);
          
          // Reload saved drafts
          loadSavedDrafts();
        }
        
        // Reset delete confirmation state
        draftToDeleteId.value = null;
        draftToDeleteSubject.value = '';
      }
    };
    
    // Original delete method (now private, used by confirmDelete)
    const deleteEmailDraft = (id: string) => {
      const draft = emailStorage.getEmailDraft(id);
      if (draft) {
        // Log the delete action
        loggingService.logEmailGeneration(draft, 'current-user', 'delete');
        
        // Delete the draft
        emailStorage.deleteEmailDraft(id);
        
        // Reload saved drafts
        loadSavedDrafts();
      }
    };
    
    const clearEmailDraft = () => {
      emailDraft.value = null;
      // Reset email fields
      emailDraftRecipient.value = '';
      emailDraftCC.value = '';
      // Keep the selectedFile so the user can parse the same file again
    };
    
    // Handle test attachment upload
    const handleTestAttachmentUpload = (event: Event) => {
      const input = event.target as HTMLInputElement;
      if (input.files && input.files.length > 0) {
        // Add the file to the attachments array
        testAttachments.value.push(input.files[0]);
        
        // Reset the input so the same file can be selected again if needed
        input.value = '';
      }
    };
    
    // Remove attachment by index
    const removeTestAttachment = (index: number) => {
      testAttachments.value.splice(index, 1);
    };
    
    // Format file size for display
    const formatFileSize = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes';
      
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };
    
    // Send test email using the email service
    const sendTestEmail = async () => {
      // Check if at least one recipient method is provided
      if (!testEmailRecipient.value && !testEmailBcc.value) {
        emailStatus.message = 'Please enter either a single recipient or BCC recipients';
        emailStatus.isError = true;
        return;
      }
      
      if (!testEmailBody.value.trim()) {
        emailStatus.message = 'Please enter email content';
        emailStatus.isError = true;
        return;
      }
      
      // Reset status
      emailStatus.message = '';
      emailStatus.isError = false;
      isEmailSending.value = true;
      
      try {
        // Prepare email data
        const emailData: any = {
          subject: 'Test Email from Verified Domain',
          body: testEmailBody.value
        };
        
        // Add recipient information based on selected mode
        if (recipientMode.value === 'single') {
          // Use TO field for single recipient mode
          emailData.to = testEmailRecipient.value;
        } else {
          // Use BCC field for multiple recipients mode
          emailData.bcc = testEmailBcc.value;
        }
        
        // Add attachments if any are selected
        if (testAttachments.value.length > 0) {
          // Convert all files to base64
          const attachmentPromises = testAttachments.value.map(async (file) => {
            const fileBase64 = await new Promise<string>((resolve) => {
              const fileReader = new FileReader();
              fileReader.onload = () => {
                const base64 = fileReader.result as string;
                // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
                const base64Content = base64.split(',')[1];
                resolve(base64Content);
              };
              fileReader.readAsDataURL(file);
            });
            
            return {
              filename: file.name,
              content: fileBase64,
              encoding: 'base64',
              contentType: file.type
            };
          });
          
          // Wait for all files to be converted
          emailData.attachments = await Promise.all(attachmentPromises);
        }
        
        // Use the centralized email service
        const result = await emailService.sendEmail(emailData);
        
        // Set status based on result
        emailStatus.message = result.message;
        emailStatus.isError = !result.success;
        
        // Log result
        if (result.success) {
          logger.info('Test email sent successfully', LogCategory.GENERAL, {
            mode: recipientMode.value,
            to: recipientMode.value === 'single' ? testEmailRecipient.value : undefined,
            bcc: recipientMode.value === 'multiple' ? 'Multiple recipients' : undefined
          });
        }
      } catch (error) {
        // This should not happen with our implementation, but just in case
        emailStatus.message = error instanceof Error ? error.message : 'An unknown error occurred';
        emailStatus.isError = true;
        
        logger.error('Error sending test email', LogCategory.GENERAL, {
          error,
          to: testEmailRecipient.value
        });
      } finally {
        isEmailSending.value = false;
      }
    };
    
    // Send email draft using the email service
    const sendEmailDraft = async () => {
      if (!emailDraft.value) {
        emailDraftStatus.message = 'No email draft to send';
        emailDraftStatus.isError = true;
        return;
      }
      
      if (!emailDraftRecipient.value) {
        emailDraftStatus.message = 'Please enter a recipient email address';
        emailDraftStatus.isError = true;
        return;
      }
      
      // Reset status
      emailDraftStatus.message = '';
      emailDraftStatus.isError = false;
      isEmailDraftSending.value = true;
      
      try {
        const recipientEmail = emailDraftRecipient.value;
        
        // Use the centralized email service
        const result = await emailService.sendEmail({
          to: recipientEmail,
          cc: emailDraftCC.value,
          subject: emailDraft.value.subject,
          body: emailDraft.value.htmlContent
        });
        
        // Set status based on result
        emailDraftStatus.message = result.message;
        emailDraftStatus.isError = !result.success;
        
        // Log result
        if (result.success) {
          logger.info('Email draft sent successfully', LogCategory.GENERAL, {
            to: recipientEmail,
            subject: emailDraft.value.subject
          });
        }
      } catch (error) {
        // This should not happen with our implementation, but just in case
        emailDraftStatus.message = error instanceof Error ? error.message : 'An unknown error occurred';
        emailDraftStatus.isError = true;
        
        logger.error('Error sending email draft', LogCategory.GENERAL, {
          error,
          draft: emailDraft.value
        });
      } finally {
        isEmailDraftSending.value = false;
      }
    };
    
    return {
      selectedFile,
      uploadError,
      emailDraft,
      savedDrafts,
      processedHtmlContent,
      handleFileUpload,
      parseFile,
      saveEmailDraft,
      loadEmailDraft,
      deleteEmailDraft,
      clearEmailDraft,
      formatDate,
      // Modals
      showDeleteModal,
      showPreviewModal,
      draftToDeleteSubject,
      showDeleteConfirmation,
      confirmDelete,
      // Test email
      recipientMode,
      testEmailRecipient,
      testEmailBcc,
      testEmailBody,
      testAttachments,
      handleTestAttachmentUpload,
      removeTestAttachment,
      formatFileSize,
      isEmailSending,
      emailStatus,
      sendTestEmail,
      // Email draft sending
      emailDraftRecipient,
      emailDraftCC,
      isEmailDraftSending,
      emailDraftStatus,
      sendEmailDraft
    };
  }
});
</script>
